import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useUserAuth } from "context/authProvider";
import Loader from "components/Loader";
import { useTenant } from "context/tenantProvider";
import { t } from "i18next";
import PrimaryButton from "components/common/Button/PrimaryButton";
import { showToast } from "utils/toast";
import axiosInstance from "utils/axios";
import { formatMessage } from "utils/utility";
import { useModal } from "context/hooks/useModal";
import OutlinedButton from "components/common/Button/OutlinedButton";
import { switchSession } from "api/user";
import { Input, Divider } from "antd";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import Google from "assets/icons/Google";
import { decrypt, encrypt } from "utils/crypto";
import { getSettings } from "store/slices";
import { useAppSelector } from "store/hooks";

const Login = () => {
  const { tenant: tenantDetail, dir } = useAppSelector(getSettings);
  const { setUser } = useUserAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [inputVales, setInputValues] = React.useState({
    phone_number: "",
    error: "",
    loading: false,
  });
  const [usageDurationPassed, setUsageDurationPassed] = React.useState(false);
  const { refreshTenant } = useTenant();
  const { showModal, hideModal } = useModal();

  function handleSubmit() {
    setInputValues({ ...inputVales, loading: true });
    if (state && Object.keys(state).length) {
      window.history.pushState({}, "");
    }
    loginApiCall();
  }

  useEffect(() => {
    if (state?.usageDurationPassed) {
      showToast(t("usageDurationPassed"), "error");
      setUsageDurationPassed(true);
      state.usageDurationPassed = false;
    }
  }, [state]);

  const loginApiCall = (params = {}) => {
    const apiPayload = {
      phone_number: inputVales.phone_number,
      ...params,
    };
    setInputValues((pre) => ({ ...pre, loading: true }));
    axiosInstance
      .post("users/login", {
        data: encrypt(apiPayload),
      })
      .then((res) => res.data)
      .then((data) => {
        setInputValues((pre) => ({ ...pre, loading: false }));
        if (data.statusCode === 200) {
          const decUserData = decrypt(data.user);
          if (decUserData.loggedIn) {
            showModal(
              <RenderUserAlreadyLoggedIn
                userDetails={{ ...decUserData, ...params }}
              />,
              "sm"
            );
            return;
          }
          handleUserLogin({ ...decUserData, ...params });
        } else if (data.statusCode === 403) {
          setInputValues({
            ...inputVales,
            error: (
              <span>
                <Link
                  to={"/user-auth/verify-email"}
                  onClick={() => {
                    axiosInstance.post(`users/resend-otp`, {
                      email: inputVales.email,
                    });
                  }}
                  state={{
                    email: inputVales.email,
                  }}
                  className="underline"
                >
                  Please verify your email first.
                </Link>
              </span>
            ),
            loading: false,
          });
          return;
        } else if (data.statusCode === 401) {
          setInputValues({
            ...inputVales,
            error: formatMessage(data.message),
            loading: false,
          });
        } else {
          if (data.message === "loginCompleteYourRegistrationFirst") {
            showToast(formatMessage(data.message), "error");
            navigate("/register", {
              state: {
                email: inputVales.email,
              },
            });
          } else if (data.message === "usageDurationPassed") {
            setUsageDurationPassed(true);
          } else {
            setInputValues({
              ...inputVales,
              error: formatMessage(data.message),
              loading: false,
            });
          }
        }
      })
      .catch((error) => {
        if (error?.response?.data?.message === "usageDurationPassed") {
          setUsageDurationPassed(true);
          setInputValues({
            ...inputVales,
            error: '',
            loading: false,
          });
        } else {
          setInputValues({
            ...inputVales,
            error: formatMessage(
              error?.response?.data?.message ?? "someThingWentWrong"
            ),
            loading: false,
          });
        }
      });
  };

  const handleUserLogin = (userDetails) => {
    if (userDetails.keys.length === 0) {
      navigate("/user-auth/add-key", {
        state: {
          email: inputVales.email,
        },
      });
      setInputValues({
        ...inputVales,
        loading: false,
      });
      return;
    }
    if (userDetails.loginType === "google") {
      setInputValues({ ...inputVales, error: "", loading: false });
      setUser(userDetails.token);
      localStorage.setItem("token", userDetails.token);
      localStorage.setItem("session", userDetails.session_id);
    } else {
      navigate("/user-auth/verify-phone");
      sessionStorage.setItem("phone_number", inputVales.phone_number);
    }
    // const isKeyPendingSave = userDetails.keys?.find(
    //   (ele) => ele.type === "occasional" && ele.status === "pending"
    // );
    // if (isKeyPendingSave) {
    //   localStorage.setItem("token", userDetails.token);
    //   localStorage.setItem("session", userDetails.session_id);
    //   setUser(userDetails.token);
    //   navigate("/activate-key");
    // } else if (!userDetails?.form_response) {
    //   localStorage.setItem("token", userDetails.token);
    //   localStorage.setItem("session", userDetails.session_id);
    //   setUser(userDetails.token);
    //   navigate(`/response-form/${userDetails._id}`);
    //   return;
    // }
    // setInputValues({ ...inputVales, error: "", loading: false });
    // setUser(userDetails.token);
    // localStorage.setItem("token", userDetails.token);
    // localStorage.setItem("session", userDetails.session_id);
    // refreshTenant();
  };

  const responseMessage = (response) => {
    axios
      .get(
        `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${response.access_token}`,
        {
          headers: {
            Authorization: `Bearer ${response.access_token}`,
            Accept: "application/json",
          },
        }
      )
      .then((res) => {
        loginApiCall({
          email: res.data.email,
          loginType: "google",
        });
      })
      .catch((err) => console.log(err));
  };

  const login = useGoogleLogin({
    onSuccess: responseMessage,
    onError: () => {
      setInputValues({
        ...inputVales,
        error: formatMessage("someThingWentWrong"),
        loading: false,
        notification: "",
      });
    },
  });

  const RenderUserAlreadyLoggedIn = ({ userDetails }) => {
    return (
      <div>
        <h2 className="text-center py-4 text-xl">
          {t("popUpTitleAccountAlreadySigned")}
        </h2>
        <div className="flex items-center justify-end w-full gap-4 mt-3">
          <OutlinedButton
            className="py-2 px-6"
            onClick={() => {
              hideModal();
            }}
          >
            {t("popUpCancelButtonAccountAlreadySigned")}
          </OutlinedButton>
          <PrimaryButton
            className="py-2 px-6 rounded-full"
            onClick={() => {
              switchSession({
                id: userDetails?._id,
                session_id: userDetails?.session_id,
                token: userDetails?.token,
              }).then((res) => {
                if (res.statusCode === 200) {
                  handleUserLogin(userDetails);
                  hideModal();
                } else {
                  showToast(formatMessage("someThingWentWrong"), "error");
                }
              });
            }}
          >
            {t("popUpConfirmButtonAccountAlreadySigned")}
          </PrimaryButton>
        </div>
      </div>
    );
  };

  return (
    <>
      <Loader open={inputVales.loading} position="portal"  />
      <div className="flex flex-col w-full" dir={"ltr"}>
        <h1 className="text-[#09244B] text-2xl font-bold text-center mb-6">
          {t("loginPageTitle")}
        </h1>
        <div className="mb-4">
          {usageDurationPassed && (
            <p className="text-red-500 text-xs italic" dir={dir}>
              {t("usageDurationPassedPleaseContact")}&nbsp;
              <a
                href={
                  tenantDetail.website?.includes("http")
                    ? tenantDetail.website
                    : `https://${tenantDetail.website}`
                }
                className="underline"
                target="_blank"
                rel="noreferrer"
              >
                {tenantDetail.website}
              </a>
            </p>
          )}
          {inputVales.error && (
            <p className="text-red-500 text-xs italic">{inputVales.error}</p>
          )}
          {inputVales?.success && (
            <p className="text-green-500 text-xs italic">
              {inputVales?.success}
            </p>
          )}
        </div>
        <div className="mb-4">
          <Input
            dir="ltr"
            id="phone"
            type="number"
            addonBefore="+972"
            placeholder={"05XXXXXXXX"}
            value={inputVales.phone_number}
            onChange={(e) => {
              const regex = /^\d{0,10}$/;
              if (
                regex.test(e.target.value) &&
                !e.target.value.includes("-")
              ) {
                setInputValues({
                  ...inputVales,
                  phone_number: e.target.value,
                });
              }
            }}
            pattern="[0-9]*"
            inputMode="numeric"
            className="phone-input py-1 text-xl"
            size="large"
          />
        </div>
        <PrimaryButton
          onClick={handleSubmit}
          disabled={!inputVales.phone_number || inputVales.phone_number.length !== 10 || inputVales.loading}
          className="!bg-gradient-to-r !from-amber-500 !to-yellow-500 hover:!from-amber-600 hover:!to-yellow-600 text-gray-900 py-3 px-7 rounded-xl disabled:!bg-gray-300 disabled:!from-gray-300 disabled:!to-gray-400 disabled:text-gray-500 disabled:cursor-not-allowed disabled:shadow-none mb-4 w-full font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
        >
          {t("login")}
        </PrimaryButton>
        <Divider variant="solid">{t("dividerOR")}</Divider>
        <button
          onClick={login}
          className="flex mb-4 items-center justify-center gap-2 border-2 border-blue-500 text-blue-500 text-base font-medium rounded-lg w-full px-5 py-2 "
        >
          <Google />
          &nbsp;{t("signInWithGoogle")}
        </button>
        {/* <div className="mb-4">
          <Input
            id="email"
            type="text"
            placeholder={t("loginEmailTextFieldPlaceholder")}
            value={inputVales.email}
            onChange={(e) =>
              setInputValues({ ...inputVales, email: e.target.value })
            }
            className="text-lg py-1"
          />
        </div>
        <div className="mb-6">
          <Input.Password
            id="password"
            placeholder={t("loginPasswordTextFieldPlaceholder")}
            value={inputVales.password}
            onChange={(e) =>
              setInputValues({ ...inputVales, password: e.target.value })
            }
            className="text-lg py-1"
          />
          <Link
            className="inline-block align-baseline font-bold text-sm text-blue-500 hover:text-blue-800 text-right w-full"
            to="/user-auth/forget-password"
          >
            {t("resetPassword")}
          </Link>
          {inputVales.error && (
            <p className="text-red-500 text-xs italic">
              {inputVales.error ?? "someThingWentWrong"}
            </p>
          )}
          {state?.error && (
            <p className="text-red-500 text-xs italic">
              {state?.error?.includes("keyAlreadyActivated")
                ? t("keyAlreadyActivated")
                : state?.error}
            </p>
          )}
          {state?.success && (
            <p className="text-green-500 text-xs italic">{state?.success}</p>
          )}
        </div>
         */}
        <div className="flex flex-col items-center justify-center w-full">
          <Link
            className="text-gray-600 underline font-medium py-1 px-4 rounded-full focus:outline-none focus:shadow-outline text-sm text-center"
            to="/register"
          >
            {t("loginRegisterButton")}
          </Link>
        </div>
      </div>
    </>
  );
};

export default Login;
